import QRCode from "qrcode";
import { paiseToRupeeString } from "@creators/backend/lib/money";

/**
 * UPI deep links, and the QR codes that carry them.
 *
 * A UPI QR is just a `upi://pay` URL encoded as an image, so any Indian payment
 * app can read it. Putting the amount and the collab reference in the link is the
 * whole point: the payer does not retype a VPA or an amount, which is where
 * paying the wrong person by a typo usually happens.
 *
 * Nothing here talks to a bank. The link only pre-fills the payer's own app.
 */

/** `name@bank`, the only shape a UPI app will accept. */
const VPA = /^[a-z0-9.\-_]{2,256}@[a-z][a-z0-9.\-]{1,64}$/i;

export function isValidUpiId(upiId: string | null | undefined): boolean {
  return typeof upiId === "string" && VPA.test(upiId.trim());
}

export function buildUpiUri(input: {
  upiId: string;
  payeeName: string;
  amountPaise: number;
  /** Collab reference, so the creator can match the credit to a collab. */
  reference?: string | null;
}): string | null {
  if (!isValidUpiId(input.upiId)) return null;

  const params = new URLSearchParams({
    pa: input.upiId.trim(),
    pn: input.payeeName.slice(0, 50),
    am: paiseToRupeeString(input.amountPaise),
    cu: "INR",
  });

  if (input.reference) {
    // `tr` is alphanumeric only in practice, and the note is what the creator
    // actually sees in their statement.
    const clean = input.reference.replace(/[^a-z0-9]/gi, "");
    if (clean) params.set("tr", clean);
    params.set("tn", `Send Collab ${input.reference}`.slice(0, 50));
  }

  // URLSearchParams writes spaces as `+`, which some UPI apps show literally
  // rather than decoding, so the payee name arrives as "Aanya+Kapoor".
  return `upi://pay?${params.toString().replace(/\+/g, "%20")}`;
}

/**
 * The link as a scannable PNG data URI, so it renders from the same HTML the
 * details came in and never needs a second request for an image of a bank
 * account.
 */
export async function upiQrDataUri(uri: string): Promise<string | null> {
  try {
    return await QRCode.toDataURL(uri, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 320,
      color: { dark: "#1c1913ff", light: "#ffffffff" },
    });
  } catch {
    return null;
  }
}

/** Link plus QR for one amount, or null when the creator has no usable UPI ID. */
export async function upiPaymentTarget(input: {
  upiId: string | null;
  payeeName: string;
  amountPaise: number;
  reference?: string | null;
}): Promise<{ uri: string; qrDataUri: string | null } | null> {
  if (!input.upiId) return null;
  const uri = buildUpiUri({ ...input, upiId: input.upiId });
  if (!uri) return null;
  return { uri, qrDataUri: await upiQrDataUri(uri) };
}
